/**
 * Utility to convert project media paths and shared drive URLs (OneDrive, Google Drive, Dropbox)
 * into direct raw media stream URLs for HTML <img> and <video> elements.
 */
export function transformMediaUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return '/assets/images/hero_home.png';

  let cleanUrl = url.trim();
  if (!cleanUrl) return '/assets/images/hero_home.png';

  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Strip localhost origin if present on dynamic upload URLs
  if (cleanUrl.includes('localhost:4200/assets/images/upload_') || cleanUrl.includes('localhost:4200/uploads/')) {
    cleanUrl = cleanUrl.replace(/http:\/\/localhost:4200/g, '');
  }

  // Handle uploaded server files (/uploads/projects/... or /assets/images/upload_...)
  if (cleanUrl.startsWith('/uploads/') || cleanUrl.startsWith('uploads/') || cleanUrl.includes('/assets/images/upload_') || cleanUrl.includes('assets/images/upload_')) {
    const relativePath = cleanUrl.startsWith('/') ? cleanUrl : '/' + cleanUrl;
    if (isLocalhost) {
      return 'https://demo.ambitionbuilt.com.au' + relativePath;
    }
    return relativePath;
  }

  // Handle static asset media directory (/assets/images/...)
  if (cleanUrl.startsWith('/assets/') || cleanUrl.startsWith('assets/')) {
    return cleanUrl.startsWith('/') ? cleanUrl : '/' + cleanUrl;
  }

  // 1. OneDrive Links Handling
  if (cleanUrl.includes('1drv.ms') || cleanUrl.includes('onedrive.live.com') || cleanUrl.includes('photos.onedrive.com')) {
    if (cleanUrl.includes('onedrive.live.com/embed') || cleanUrl.includes('download=1')) {
      return cleanUrl;
    }

    if (cleanUrl.includes('1drv.ms')) {
      const cleanShareUrl = cleanUrl.split('?')[0];
      try {
        const base64 = btoa(cleanShareUrl);
        const safeBase64 = base64.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
        return `https://api.onedrive.com/v1.0/shares/u!${safeBase64}/root/content`;
      } catch (e) {
        return cleanUrl;
      }
    }
  }

  // 2. Google Drive Shared Links Transformation
  if (cleanUrl.includes('drive.google.com')) {
    const fileIdMatch = cleanUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (fileIdMatch && fileIdMatch[1]) {
      return `https://lh3.googleusercontent.com/d/${fileIdMatch[1]}`;
    }
  }

  // 3. Dropbox Shared Links Transformation
  if (cleanUrl.includes('dropbox.com')) {
    return cleanUrl.replace('dl=0', 'raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
  }

  return cleanUrl;
}

/**
 * Image error handler fallback to prevent broken image icons in UI
 */
export function onImageError(event: Event, fallbackUrl: string = '/assets/images/hero_home.png') {
  const imgElement = event.target as HTMLImageElement;
  if (imgElement && imgElement.src !== fallbackUrl) {
    imgElement.src = fallbackUrl;
  }
}
